import { Campaign, Collab, FoodfluenceLocation, usesOpenHoursScheduling, scheduleSlotKeyToPickerDate, getCalendarDatePartsInTimezone, calendarDayToUtcMidnightKey, deriveTurnaroundDays } from '@foodfluence/shared';
import { SelectedLocation } from './types';
import { getTimezone, convertToLocalTime } from './timezoneUtils';
import { getAvailableDatesAndTimes } from './timeOptionsUtils';
import { getCollabAvailableTimes } from "../../../../utils/collabUtils";
import { toDate } from "../../../../utils/dateUtils";
import { getMaxPartySize, isValidPartySize } from './partySizeUtils';

export interface ValidationParams {
  selectedDate: Date | null;
  selectedTime: string;
  selectedLocation: SelectedLocation | null;
  partySize: number | null;
  initialDate: Date | null;
  initialTime: string;
  initialLocation: SelectedLocation | null;
  initialPartySize: number | null;
  turnaroundDays?: number | null;
  initialTurnaroundDays?: number | null;
  campaign?: Campaign;
  locations?: FoodfluenceLocation[];
}


const isLocationValid = (selectedLocation: SelectedLocation | null, locations?: FoodfluenceLocation[]): boolean => {
  if (!locations || locations.length === 0) return true;
  return !!selectedLocation?.docId;
};

const isPartySizeValid = (partySize: number | null, campaign?: Campaign): boolean => {
  // Chosen AND within the brand's cap — blocks a stale reschedule value above a lowered max.
  return isValidPartySize(partySize, getMaxPartySize(campaign));
};

/** Compare selected day + time (timezone-adjusted) to now. */
const isDateTimeValid = (
  selectedDate: Date | null,
  selectedTime: string,
  selectedLocation: SelectedLocation | null,
  locations: FoodfluenceLocation[] = []
): boolean => {
  if (!selectedDate || !selectedTime) return false;
  const timezone = getTimezone(selectedLocation?.docId, locations);
  const { localTime } = convertToLocalTime(selectedDate, selectedTime, timezone.value);
  return localTime > new Date();
};

const hasLocationChanges = (
  selectedLocation: SelectedLocation | null,
  initialLocation: SelectedLocation | null,
  campaign?: Campaign
): boolean => {
  if (!campaign?.foodfluenceLocations || campaign.foodfluenceLocations.length === 0) return false;
  if (!initialLocation) return !!selectedLocation;
  return selectedLocation?.docId !== initialLocation?.docId;
};

const hasDateTimeChanges = (
  selectedDate: Date | null,
  selectedTime: string,
  initialDate: Date | null,
  initialTime: string
): boolean => {
  if (!initialDate || !initialTime) return true;
  return selectedDate?.getTime() !== initialDate?.getTime() || selectedTime !== initialTime;
};

const hasPartySizeChanges = (
  partySize: number | null,
  initialPartySize: number | null
): boolean => {
  if (!initialPartySize) return true;
  return partySize !== initialPartySize;
};

const hasTurnaroundChanges = (
  turnaroundDays?: number | null,
  initialTurnaroundDays?: number | null
): boolean => {
  // Only counts as a change once a turnaround is actually chosen; a null pick never enables submit
  // on its own. On a reschedule the baseline is the previously chosen turnaround, so switching it
  // (e.g. keeping the same slot but changing the post window) registers as a change.
  if (turnaroundDays == null) return false;
  return turnaroundDays !== (initialTurnaroundDays ?? null);
};

export const isSubmitDisabled = ({
  selectedDate,
  selectedTime,
  selectedLocation,
  partySize,
  initialDate,
  initialTime,
  initialLocation,
  initialPartySize,
  turnaroundDays,
  initialTurnaroundDays,
  campaign,
  locations
}: ValidationParams): boolean => {
  // Check validity
  const isValid = isDateTimeValid(selectedDate, selectedTime, selectedLocation, locations || []) &&
                 isLocationValid(selectedLocation, locations) &&
                 isPartySizeValid(partySize, campaign);

  const hasChanges = hasDateTimeChanges(selectedDate, selectedTime, initialDate, initialTime) ||
                    hasLocationChanges(selectedLocation, initialLocation, campaign) ||
                    hasPartySizeChanges(partySize, initialPartySize) ||
                    hasTurnaroundChanges(turnaroundDays, initialTurnaroundDays);

  return !isValid || !hasChanges;
};

export const initializeSchedulingData = (
  collab?: Collab,
  campaign?: Campaign | null,
  locations: FoodfluenceLocation[] = [],
  /** The wizard's resolved turnaround window, so the baseline matches what the picker restores. */
  maxTurnaroundDays?: number
) => {
  let selectedDate: Date | null = null;
  let selectedTime = '';
  let selectedLocation: { docId: string; fullAddress: string; } | null = null;

  // Auto-select location if there's only one available
  if (locations && locations.length === 1) {
    selectedLocation = {
      docId: locations[0].docId,
      fullAddress: locations[0].fullAddress
    };
  } else if (collab?.selectedLocation && locations) {
    const location = locations.find(loc => loc.docId === collab.selectedLocation);
    if (location) {
      selectedLocation = {
        docId: location.docId,
        fullAddress: location.fullAddress
      };
    }
  }

  const effectiveLocationId = selectedLocation?.docId ?? collab?.selectedLocation;
  const timezone = getTimezone(effectiveLocationId, locations);

  // Handle existing scheduled date in the location's timezone (not browser local).
  // toDate (not a raw `.seconds` read) because the field arrives as a Firestore Timestamp, its REST
  // `_seconds` form, or — in older docs — a blank value: reading `.seconds` off those produced an
  // Invalid Date, which then threw inside Intl and left Review with nothing to render.
  const scheduledDate = toDate(collab?.scheduledCollabDate);
  if (scheduledDate) {
    // 2-digit, not numeric: slot options are zero-padded ("02:00 PM"), so "2:00 PM"
    // would never match an <option value> and the dropdown would render empty.
    const timeString = scheduledDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone.value,
    });
    const { year, month, day } = getCalendarDatePartsInTimezone(scheduledDate, timezone.value);
    selectedDate = scheduleSlotKeyToPickerDate(calendarDayToUtcMidnightKey(year, month, day));
    selectedTime = timeString;
  } else if (campaign && !usesOpenHoursScheduling(campaign, collab)) {
    // Open-hours campaigns load slots from the backend; only pre-select for specific-times mode.
    const { availableTimes, hasSpecificTimeIntervals } = getCollabAvailableTimes(collab, campaign);
    const timeOptions = getAvailableDatesAndTimes(
      hasSpecificTimeIntervals,
      campaign,
      collab,
      locations,
      availableTimes,
      effectiveLocationId
    );

    // If there's only one available date, select it
    const availableDates = Array.from(timeOptions.keys());
    if (availableDates.length === 1) {
      const dateKey = availableDates[0];
      selectedDate = scheduleSlotKeyToPickerDate(dateKey);
      const timesForDate = timeOptions.get(dateKey) || [];
      if (timesForDate.length === 1) {
        selectedTime = timesForDate[0].time;
      }
    }
  }

  let initialPartySize = null;
  if (collab?.partySize) {
    // Clamp a stored value to the brand's current cap — a reschedule pre-fill (e.g. 4) must not
    // survive the brand lowering its max (e.g. to 2), which would otherwise pass the gate invisibly.
    initialPartySize = Math.min(collab.partySize, getMaxPartySize(campaign ?? undefined));
  } else if (campaign?.maxPartySize == 1) {
    initialPartySize = 1;
  }

  return {
    selectedDate,
    selectedTime,
    selectedLocation,
    initialDate: selectedDate,
    initialTime: selectedTime,
    initialPartySize: initialPartySize,
    initialLocation: selectedLocation,
    // Derived, not stored — see deriveTurnaroundDays. This is the baseline the unsaved-changes
    // check compares against, so it must match what the wizard defaults the picker to.
    initialTurnaroundDays: deriveTurnaroundDays(
      toDate(collab?.scheduledCollabDate),
      collab?.scheduledPostingDate ? new Date(collab.scheduledPostingDate) : null,
      maxTurnaroundDays
    ),
    // Start the location search empty so the card picker shows every location by default; the
    // pre-selected one is surfaced via highlight (selectedLocationId), not by pre-filtering the
    // list to its address (which collapsed the list until the user cleared the box).
    locationSearchQuery: ''
  };
}; 